// Espelho do backend (api_dponet: RichTexts::SanitizeStyle): fica só a formatação (cor, fonte, espaçamento, borda,
// tamanho), cada propriedade com o valor validado e com teto. Sai o que tira o conteúdo do lugar ou o esconde
// (position, z-index, transform, margem negativa, display none, opacity), o que carrega recurso externo (url()) e
// qualquer valor fora do formato esperado (escape \, comentário /*, expression()).

import { isEmpty } from 'lodash'

const COLOR = /^(#[0-9a-f]{3,8}|(rgba?|hsla?)\([\d\s.,%/]+\)|[a-z]+)$/
const LENGTH = /^(\d+(?:\.\d+)?)(px|pt|em|rem|%)?$/
// Teto por unidade; a chave '' é o número sem unidade (só zero onde não há teto para ele).
const SPACING_LIMITS = { px: 200, pt: 150, em: 12, rem: 12, '%': 50, '': 0 }
const FONT_SIZE_LIMITS = { px: 72, pt: 54, em: 4.5, rem: 4.5, '%': 450 }
const LINE_HEIGHT_LIMITS = { px: 100, pt: 75, em: 5, rem: 5, '%': 500, '': 5 }
const LETTER_SPACING_LIMITS = { px: 20, pt: 15, em: 1, rem: 1, '': 0 }
const BORDER_WIDTH_LIMITS = { px: 10, pt: 8, em: 1, rem: 1, '': 0 }
const BORDER_RADIUS_LIMITS = { px: 50, pt: 38, em: 3, rem: 3, '%': 50, '': 0 }
const SIZE_LIMITS = { px: 2000, pt: 1500, em: 125, rem: 125, '%': 100, '': 0 }

const FONT_SIZE_KEYWORDS = [
  'xx-small',
  'x-small',
  'small',
  'medium',
  'large',
  'x-large',
  'xx-large',
  'smaller',
  'larger',
]
const BORDER_WIDTH_KEYWORDS = ['thin', 'medium', 'thick']
const BORDER_STYLES = [
  'none',
  'hidden',
  'solid',
  'dashed',
  'dotted',
  'double',
  'groove',
  'ridge',
  'inset',
  'outset',
]
const TEXT_DECORATIONS = ['none', 'underline', 'overline', 'line-through']
const FONT_FAMILIES = [
  'arial',
  'helvetica',
  'times new roman',
  'times',
  'georgia',
  'verdana',
  'tahoma',
  'trebuchet ms',
  'courier new',
  'courier',
  'calibri',
  'cambria',
  'garamond',
  'segoe ui',
  'roboto',
  'open sans',
  'sans-serif',
  'serif',
  'monospace',
]
const KEYWORD_PROPERTIES = {
  'font-weight': [
    'normal',
    'bold',
    'bolder',
    'lighter',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
  ],
  'font-style': ['normal', 'italic', 'oblique'],
  'text-align': ['left', 'center', 'right', 'justify', 'start', 'end'],
  'vertical-align': [
    'baseline',
    'sub',
    'super',
    'top',
    'middle',
    'bottom',
    'text-top',
    'text-bottom',
  ],
  float: ['left', 'right', 'none'],
  display: ['block', 'inline', 'inline-block'],
  'border-collapse': ['collapse', 'separate'],
  'list-style-type': [
    'none',
    'disc',
    'circle',
    'square',
    'decimal',
    'lower-alpha',
    'upper-alpha',
    'lower-latin',
    'upper-latin',
    'lower-roman',
    'upper-roman',
  ],
}
const SIDES = ['top', 'right', 'bottom', 'left']
// Separa os valores de um atalho (margin, border...) sem quebrar uma função: "1px solid rgb(0, 0, 0)".
const VALUE_TOKENS = /[^\s(]+\([^)]*\)|\S+/g

const withSides = (property) => [
  property,
  ...SIDES.map((side) => `${property}-${side}`),
]

const isLength = (value, limits) => {
  const match = value.match(LENGTH)
  if (!match) return false

  const unit = match[2] || ''
  return unit in limits && Number(match[1]) <= limits[unit]
}

const isColor = (value) => COLOR.test(value)

const isBorderWidth = (value) =>
  BORDER_WIDTH_KEYWORDS.includes(value) || isLength(value, BORDER_WIDTH_LIMITS)

const allTokens = (value, maxTokens, isValid) => {
  const tokens = value.match(VALUE_TOKENS) || []
  return (
    tokens.length >= 1 && tokens.length <= maxTokens && tokens.every(isValid)
  )
}

const VALUE_VALIDATORS = {
  color: isColor,
  'background-color': isColor,
  'font-size': (value) =>
    FONT_SIZE_KEYWORDS.includes(value) || isLength(value, FONT_SIZE_LIMITS),
  'line-height': (value) =>
    value === 'normal' || isLength(value, LINE_HEIGHT_LIMITS),
  'letter-spacing': (value) =>
    value === 'normal' || isLength(value, LETTER_SPACING_LIMITS),
  'text-indent': (value) => isLength(value, SPACING_LIMITS),
  'text-decoration': (value) =>
    allTokens(value, 3, (token) => TEXT_DECORATIONS.includes(token)),
  'text-decoration-line': (value) =>
    allTokens(value, 3, (token) => TEXT_DECORATIONS.includes(token)),
  'border-color': (value) => allTokens(value, 4, isColor),
  'border-width': (value) => allTokens(value, 4, isBorderWidth),
  'border-style': (value) =>
    allTokens(value, 4, (token) => BORDER_STYLES.includes(token)),
  'border-radius': (value) =>
    allTokens(value, 4, (token) => isLength(token, BORDER_RADIUS_LIMITS)),
  ...Object.fromEntries(
    ['width', 'height', 'max-width'].map((property) => [
      property,
      (value) => value === 'auto' || isLength(value, SIZE_LIMITS),
    ]),
  ),
  ...Object.fromEntries(
    withSides('margin').map((property) => [
      property,
      (value) =>
        allTokens(
          value,
          4,
          (token) => token === 'auto' || isLength(token, SPACING_LIMITS),
        ),
    ]),
  ),
  ...Object.fromEntries(
    withSides('padding').map((property) => [
      property,
      (value) =>
        allTokens(value, 4, (token) => isLength(token, SPACING_LIMITS)),
    ]),
  ),
  ...Object.fromEntries(
    withSides('border').map((property) => [
      property,
      (value) =>
        allTokens(
          value,
          3,
          (token) =>
            isBorderWidth(token) ||
            BORDER_STYLES.includes(token) ||
            isColor(token),
        ),
    ]),
  ),
  ...Object.fromEntries(
    Object.entries(KEYWORD_PROPERTIES).map(([property, keywords]) => [
      property,
      (value) => keywords.includes(value),
    ]),
  ),
}

// Fica só a parte da lista de fontes que é conhecida; sem nenhuma, a declaração sai.
const sanitizeFontFamily = (value) => {
  const families = value
    .split(',')
    .map((family) => family.trim().replace(/['"]/g, ''))
    .filter((family) => FONT_FAMILIES.includes(family))
  if (isEmpty(families)) return null

  return families
    .map((family) => (family.includes(' ') ? `'${family}'` : family))
    .join(', ')
}

const sanitizeDeclaration = (declaration) => {
  const separatorIndex = declaration.indexOf(':')
  if (separatorIndex === -1) return null

  const normalize = (part) => part.trim().toLowerCase().replace(/ +/g, ' ')
  const property = normalize(declaration.slice(0, separatorIndex))
  const value = normalize(declaration.slice(separatorIndex + 1))
  if (!property || !value) return null

  if (property === 'font-family') {
    const families = sanitizeFontFamily(value)
    return families && `${property}: ${families};`
  }

  const isValid = Object.prototype.hasOwnProperty.call(
    VALUE_VALIDATORS,
    property,
  )
  return isValid && VALUE_VALIDATORS[property](value)
    ? `${property}: ${value};`
    : null
}

// Devolve as declarações aceitas ("color: red; font-size: 14px;") ou null quando nada sobra.
const sanitizeStyle = (style = '') => {
  const declarations = style.split(';').map(sanitizeDeclaration).filter(Boolean)

  return isEmpty(declarations) ? null : declarations.join(' ')
}

export default sanitizeStyle
